<template>
    <div
        class="effect_slider"
        :class="[variantClass, { effect_slider_dragging: isDragging }]"
        @wheel.prevent="handleWheel"
        @dblclick="resetToDefault"
    >
        <div class="slider_head">
            <div class="slider_label_group">
                <span class="slider_label">{{ label }}</span>
                <span v-if="hint" class="slider_hint">{{ hint }}</span>
            </div>
            <span class="slider_value">{{ displayValue }}</span>
        </div>

        <div class="slider_surface">
            <div
                ref="track"
                class="slider_track"
                @pointerdown="startDrag"
            >
                <div v-if="showTicks" class="slider_ticks" aria-hidden="true">
                    <span
                        v-for="tick in tickCount"
                        :key="tick"
                        class="slider_tick"
                        :style="{ left: `${((tick - 1) / (tickCount - 1)) * 100}%` }"
                    ></span>
                </div>

                <span class="slider_baseline"></span>
                <span
                    v-if="showDefaultMarker"
                    class="slider_default_marker"
                    :style="{ left: `${anchorRatio * 100}%` }"
                ></span>
                <span class="slider_fill" :style="fillStyle"></span>
                <span class="slider_thumb" :style="{ left: `${ratio * 100}%` }"></span>
            </div>

            <div v-if="variant !== 'minimal'" class="slider_microcopy">
                horizontal drag · wheel · double click reset
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'EffectSlider',
    props: {
        modelValue: {
            type: [Number, String],
            default: 0
        },
        min: {
            type: Number,
            default: 0
        },
        max: {
            type: Number,
            default: 1
        },
        step: {
            type: Number,
            default: 0.01
        },
        defaultValue: {
            type: [Number, String],
            default: 0
        },
        label: {
            type: String,
            default: ''
        },
        hint: {
            type: String,
            default: ''
        },
        valueLabel: {
            type: String,
            default: ''
        },
        unit: {
            type: String,
            default: ''
        },
        precision: {
            type: Number,
            default: null
        },
        variant: {
            type: String,
            default: 'glow',
            validator(value) {
                return ['glow', 'console', 'minimal'].includes(value);
            }
        },
        fillFromMin: {
            type: Boolean,
            default: false
        },
        tickCount: {
            type: Number,
            default: 21
        }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            isDragging: false
        };
    },
    computed: {
        variantClass() {
            return `effect_slider_${this.variant}`;
        },
        range() {
            return this.max - this.min;
        },
        numericValue() {
            const parsed = Number(this.modelValue);
            return Number.isFinite(parsed) ? parsed : this.min;
        },
        defaultNumericValue() {
            const parsed = Number(this.defaultValue);
            return this.clampValue(Number.isFinite(parsed) ? parsed : this.min);
        },
        actualPrecision() {
            if (this.precision !== null) {
                return this.precision;
            }

            const stepAsString = String(this.step);
            if (!stepAsString.includes('.')) {
                return 0;
            }

            return stepAsString.split('.')[1].length;
        },
        ratio() {
            if (this.range <= 0) {
                return 0;
            }

            return this.clampRatio((this.numericValue - this.min) / this.range);
        },
        anchorRatio() {
            if (this.range <= 0) {
                return 0;
            }

            return this.clampRatio((this.defaultNumericValue - this.min) / this.range);
        },
        showDefaultMarker() {
            return this.anchorRatio > 0 && this.anchorRatio < 1;
        },
        fillBorderRadius() {
            if (this.variant !== 'minimal') {
                return undefined;
            }

            const knobIsOnLeft = !this.fillFromMin && this.ratio < this.anchorRatio;
            return knobIsOnLeft ? '0 999px 999px 0' : '999px 0 0 999px';
        },
        fillStyle() {
            if (this.fillFromMin) {
                return {
                    left: '0%',
                    width: `${this.ratio * 100}%`,
                    borderRadius: this.fillBorderRadius
                };
            }

            const left = Math.min(this.ratio, this.anchorRatio);
            const width = Math.abs(this.ratio - this.anchorRatio);

            return {
                left: `${left * 100}%`,
                width: `${Math.max(width * 100, 0.0001)}%`,
                borderRadius: this.fillBorderRadius
            };
        },
        showTicks() {
            return this.variant !== 'minimal';
        },
        displayValue() {
            if (this.valueLabel) {
                return this.valueLabel;
            }

            return `${this.numericValue.toFixed(this.actualPrecision)}${this.unit}`;
        }
    },
    methods: {
        clampRatio(value) {
            return Math.max(0, Math.min(1, value));
        },
        clampValue(value) {
            return Math.max(this.min, Math.min(this.max, value));
        },
        snapValue(value) {
            if (!this.step || this.step <= 0) {
                return this.clampValue(value);
            }

            const stepped = Math.round((value - this.min) / this.step) * this.step + this.min;
            const factor = 10 ** this.actualPrecision;
            return Math.round(this.clampValue(stepped) * factor) / factor;
        },
        emitValue(value) {
            this.$emit('update:modelValue', this.snapValue(value));
        },
        updateFromClientX(clientX) {
            const track = this.$refs.track;
            if (!track || this.range <= 0) {
                return;
            }

            const rect = track.getBoundingClientRect();
            const rawRatio = (clientX - rect.left) / rect.width;
            const nextValue = this.min + this.clampRatio(rawRatio) * this.range;
            this.emitValue(nextValue);
        },
        handleWheel(event) {
            const direction = event.deltaY > 0 ? -1 : 1;
            const wheelStep = event.shiftKey ? this.step * 5 : this.step;
            this.emitValue(this.numericValue + direction * wheelStep);
        },
        startDrag(event) {
            if (event.button !== undefined && event.button !== 0) {
                return;
            }

            event.preventDefault();
            this.isDragging = true;
            this.updateFromClientX(event.clientX);

            window.addEventListener('pointermove', this.handlePointerMove);
            window.addEventListener('pointerup', this.stopDrag);
            window.addEventListener('pointercancel', this.stopDrag);
        },
        handlePointerMove(event) {
            if (!this.isDragging) {
                return;
            }

            this.updateFromClientX(event.clientX);
        },
        stopDrag() {
            this.isDragging = false;
            window.removeEventListener('pointermove', this.handlePointerMove);
            window.removeEventListener('pointerup', this.stopDrag);
            window.removeEventListener('pointercancel', this.stopDrag);
        },
        resetToDefault() {
            this.emitValue(this.defaultNumericValue);
        }
    },
    beforeUnmount() {
        this.stopDrag();
    }
};
</script>

<style scoped>
.effect_slider {
    --surface-bg: linear-gradient(180deg, #23201c 0%, #171513 100%);
    --surface-border: rgba(122, 97, 66, 0.36);
    --label-color: #f1f1f1;
    --hint-color: #7d7d7d;
    --value-color: #f6d4aa;
    --track-bg: rgba(255, 255, 255, 0.07);
    --tick-color: rgba(255, 255, 255, 0.08);
    --marker-color: rgba(255, 255, 255, 0.18);
    --fill-bg: linear-gradient(90deg, #f4ca86 0%, #eb8c44 100%);
    --fill-glow: rgba(244, 202, 134, 0.28);
    --thumb-bg: linear-gradient(180deg, #fff2dc 0%, #df9d58 100%);
    --thumb-border: rgba(255, 255, 255, 0.18);
    --thumb-shadow: 0 8px 18px rgba(0, 0, 0, 0.3);
    --track-height: 0.72rem;
    --fill-height: 0.72rem;
    --thumb-width: 1rem;
    --thumb-height: 1.35rem;
    --thumb-radius: 0.4rem;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    width: 100%;
}

.effect_slider_console {
    --surface-bg: linear-gradient(180deg, #1f2328 0%, #14171a 100%);
    --surface-border: rgba(124, 152, 178, 0.22);
    --value-color: #c9ebff;
    --track-bg: linear-gradient(180deg, rgba(89, 101, 116, 0.42), rgba(30, 35, 40, 0.92));
    --tick-color: rgba(190, 214, 235, 0.1);
    --marker-color: rgba(200, 228, 255, 0.2);
    --fill-bg: linear-gradient(90deg, #6fc9ff 0%, #7bf2c6 100%);
    --fill-glow: rgba(111, 201, 255, 0.24);
    --thumb-bg: linear-gradient(180deg, #d7e3ec 0%, #7991a7 100%);
    --thumb-border: rgba(255, 255, 255, 0.1);
    --track-height: 0.8rem;
    --fill-height: 0.8rem;
    --thumb-width: 0.82rem;
    --thumb-height: 1.45rem;
    --thumb-radius: 0.22rem;
}

.effect_slider_minimal {
    --surface-bg: transparent;
    --surface-border: transparent;
    --label-color: #a3a3a3;
    --hint-color: #a3a3a3;
    --value-color: #a3a3a3;
    --track-bg: #2f2f2f;
    --tick-color: transparent;
    --marker-color: rgba(255, 255, 255, 0.2);
    --fill-bg: #fff;
    --fill-glow: transparent;
    --thumb-bg: #fff;
    --thumb-border: #1e1e1e;
    --thumb-shadow: none;
    --track-height: 1.125rem;
    --fill-height: 1.125rem;
    --thumb-width: 1.125rem;
    --thumb-height: 1.125rem;
    --thumb-radius: 999px;
}

.slider_head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
}

.slider_label_group {
    display: flex;
    align-items: baseline;
    gap: 0.55rem;
    min-width: 0;
}

.slider_label {
    color: var(--label-color);
    font-weight: 700;
}

.slider_hint {
    color: var(--hint-color);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.slider_value {
    color: var(--value-color);
    font-size: 0.9rem;
    white-space: nowrap;
}

.slider_surface {
    position: relative;
    padding: 0.9rem 1rem 0.8rem;
    border-radius: 1rem;
    background: var(--surface-bg);
    border: 1px solid var(--surface-border);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.effect_slider_minimal .slider_surface {
    padding: 0.45rem 0 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
}

.slider_track {
    position: relative;
    height: 1.55rem;
    cursor: ew-resize;
    touch-action: none;
}

.slider_ticks {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.slider_tick {
    position: absolute;
    top: 50%;
    width: 1px;
    height: 0.8rem;
    transform: translate(-50%, -50%);
    background: var(--tick-color);
}

.slider_baseline,
.slider_fill {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 999px;
}

.slider_baseline {
    left: 0;
    right: 0;
    height: var(--track-height);
    background: var(--track-bg);
}

.slider_fill {
    height: var(--fill-height);
    background: var(--fill-bg);
    box-shadow: 0 0 16px var(--fill-glow);
    min-width: 2px;
}

.slider_default_marker {
    position: absolute;
    top: 50%;
    width: 2px;
    height: 1rem;
    transform: translate(-50%, -50%);
    background: var(--marker-color);
    border-radius: 999px;
}

.effect_slider_minimal .slider_default_marker {
    height: 0.7rem;
}

.slider_thumb {
    position: absolute;
    top: 50%;
    width: var(--thumb-width);
    height: var(--thumb-height);
    transform: translate(-50%, -50%);
    border-radius: var(--thumb-radius);
    background: var(--thumb-bg);
    border: 1px solid var(--thumb-border);
    box-shadow: var(--thumb-shadow);
    transition: transform 140ms ease, box-shadow 140ms ease;
}

.effect_slider_console .slider_thumb {
    box-shadow:
        0 8px 18px rgba(0, 0, 0, 0.32),
        inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.effect_slider_minimal .slider_thumb {
    box-shadow: var(--thumb-shadow);
}

.effect_slider_dragging .slider_thumb {
    transform: translate(-50%, -50%) scale(1.04);
}

.slider_microcopy {
    margin-top: 0.55rem;
    color: rgba(255, 255, 255, 0.34);
    font-size: 0.68rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

@media (max-width: 576px) {
    .slider_head {
        gap: 0.7rem;
    }

    .slider_label_group {
        flex-wrap: wrap;
        gap: 0.35rem 0.55rem;
    }
}
</style>
